import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from 'contexts/AuthContext';
import { supabase } from 'lib/supabase';
import Header from 'components/ui/Header';
import Icon from 'components/AppIcon';
import Footer from 'pages/landing-page/components/Footer';

const formatPrice = (value) => {
  const num = parseFloat(value) || 0;
  return '€ ' + num?.toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  return new Date(dateStr)?.toLocaleDateString('it-IT', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  });
};

const StarRating = ({ rating }) => {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5]?.map((star) => (
        <Icon
          key={star}
          name="Star"
          size={14}
          className={star <= rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}
        />
      ))}
      <span className="ml-1 text-xs text-gray-500">{rating}/5</span>
    </div>
  );
};

const ProfessionalLavoriConfermati = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [confirmedJobs, setConfirmedJobs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      navigate('/login-form');
      return;
    }
    fetchConfirmedJobs();
  }, [user]);

  const fetchConfirmedJobs = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase?.from('reviews')?.select(`
          id, rating, text, created_at,
          quotes!inner(id, preview_price, preview_text),
          service_requests!inner(id, title, service_type, location_name)
        `)?.eq('professional_id', user?.id)?.order('created_at', { ascending: false });

      if (error) throw error;
      setConfirmedJobs(data || []);
    } catch (err) {
      console.error('Error fetching confirmed jobs:', err);
      setConfirmedJobs([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-gray-50 pt-16">
      <Header />
      <div className="flex-1 w-full px-6 md:px-12 lg:px-16 py-8">
        {/* Back button */}
        <button
          onClick={() => navigate('/professional-dashboard')}
          className="inline-flex items-center gap-2 text-gray-500 hover:text-[#2780b5] mb-6 transition-colors"
        >
          <Icon name="ArrowLeft" size={18} />
          <span className="text-sm font-medium">Torna alla dashboard</span>
        </button>

        {/* Page title */}
        <h1 className="text-xl font-bold text-[#013776] pl-4 border-l-4 border-[#2780b5]">
          Lavori confermati
        </h1>
        <p className="mt-2 mb-6 text-sm text-gray-600">
          Preventivi confermati dai clienti tramite recensione
        </p>

        {/* Loading state */}
        {loading && (
          <div className="flex items-center justify-center py-16">
            <div className="w-8 h-8 border-2 border-[#2780b5] border-t-transparent rounded-full animate-spin" />
          </div>
        )}

        {/* Empty state */}
        {!loading && confirmedJobs?.length === 0 && (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <div className="w-14 h-14 rounded-full bg-blue-50 flex items-center justify-center mb-4">
              <Icon name="CheckCircle" size={28} className="text-[#2780b5]" />
            </div>
            <p className="text-base font-semibold text-gray-700">Nessun lavoro confermato ancora.</p>
            <p className="mt-1 text-sm text-gray-500 max-w-sm">
              Quando un cliente lascia una recensione su un tuo preventivo, comparirà qui.
            </p>
          </div>
        )}

        {/* Cards grid */}
        {!loading && confirmedJobs?.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {confirmedJobs?.map((job) => (
              <div
                key={job?.id}
                className="bg-white rounded-xl border border-gray-200 p-6"
              >
                {/* Title */}
                <h2 className="text-base font-semibold text-[#013776] mb-1 leading-snug">
                  {job?.service_requests?.title}
                </h2>

                {/* Subtitle line */}
                <p className="text-xs text-gray-500 mb-3">
                  {job?.service_requests?.service_type}
                  {job?.service_requests?.location_name ? ` · ${job?.service_requests?.location_name}` : ''}
                </p>

                {/* Price */}
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-sm font-bold text-[#2780b5]">
                    {formatPrice(job?.quotes?.preview_price)}
                  </span>
                </div>

                {/* Star rating */}
                <div className="mb-3">
                  <StarRating rating={job?.rating || 0} />
                </div>

                {/* Review text (truncated) */}
                {job?.text && (
                  <p className="text-sm text-gray-600 italic mb-3 leading-relaxed">
                    "{job?.text?.length > 120 ? job?.text?.slice(0, 120) + '…' : job?.text}"
                  </p>
                )}

                {/* Date */}
                <p className="text-xs text-gray-400">
                  Confermato il {formatDate(job?.created_at)}
                </p>
              </div>
            ))}
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
};

export default ProfessionalLavoriConfermati;
